/**
 * MatrizPermissoesView Component - Permissions matrix by profile
 */
import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import type { MatrizPermissoes } from '../../services/permissoesService';
import type { Perfil } from '../../services/perfisService';

type TipoPermissao = 'pode_visualizar' | 'pode_criar' | 'pode_editar' | 'pode_excluir' | 'pode_exportar';

interface MatrizPermissoesViewProps {
  matriz: MatrizPermissoes;
  perfis: Perfil[];
  onPermissaoChange: (
    perfilId: number,
    telaId: number,
    tipoPermissao: TipoPermissao,
    valor: boolean
  ) => void;
}

const tiposPermissao = [
  { key: 'pode_visualizar' as const, label: 'V', title: 'Visualizar', color: 'blue' },
  { key: 'pode_criar' as const, label: 'C', title: 'Criar', color: 'green' },
  { key: 'pode_editar' as const, label: 'E', title: 'Editar', color: 'yellow' },
  { key: 'pode_excluir' as const, label: 'D', title: 'Excluir', color: 'red' },
  { key: 'pode_exportar' as const, label: 'X', title: 'Exportar', color: 'purple' }
];

export const MatrizPermissoesView: React.FC<MatrizPermissoesViewProps> = ({
  matriz,
  perfis,
  onPermissaoChange
}) => {
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());

  // Group screens by module
  const telasPorModulo = matriz.telas.reduce((acc, tela) => {
    if (!acc[tela.modulo]) {
      acc[tela.modulo] = [];
    }
    acc[tela.modulo].push(tela);
    return acc;
  }, {} as Record<string, typeof matriz.telas>);

  const modulos = Object.keys(telasPorModulo).sort();

  const toggleModule = (modulo: string) => {
    setExpandedModules(prev => {
      const next = new Set(prev);
      if (next.has(modulo)) {
        next.delete(modulo);
      } else {
        next.add(modulo);
      }
      return next;
    });
  };

  const getPermissao = (perfilId: number, telaId: number, tipo: string) => {
    const permissao = matriz.matriz.find(
      p => p.perfil_id === perfilId && p.tela_id === telaId
    );
    return permissao ? (permissao as any)[tipo] : false;
  };

  return (
    <div className="space-y-3">
      {/* Legend */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <p className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
          Legenda de Permissões:
        </p>
        <div className="flex flex-wrap gap-4 text-sm">
          {tiposPermissao.map(tipo => (
            <div key={tipo.key} className="flex items-center gap-2">
              <span className={`w-6 h-6 rounded flex items-center justify-center font-bold text-white bg-${tipo.color}-600`}>
                {tipo.label}
              </span>
              <span className="text-gray-700 dark:text-gray-300">{tipo.title}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Matrix by Module */}
      {modulos.map(modulo => (
        <div key={modulo} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Module Header - Accordion */}
          <button
            onClick={() => toggleModule(modulo)}
            className="w-full bg-gray-100 dark:bg-gray-700 px-4 py-2 border-b border-gray-200 dark:border-gray-600 flex items-center justify-between hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              {modulo} ({telasPorModulo[modulo].length})
            </h3>
            <ChevronDown
              className={`w-5 h-5 text-gray-600 dark:text-gray-400 transition-transform ${
                expandedModules.has(modulo) ? 'rotate-180' : ''
              }`}
            />
          </button>

          {/* Permissions Table - Conditional */}
          {expandedModules.has(modulo) && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider sticky left-0 bg-gray-50 dark:bg-gray-700/50 z-10">
                      Tela
                    </th>
                    {matriz.perfis.map(perfil => (
                      <th key={perfil.id} className="px-2 py-2 text-center">
                        <div className="text-xs font-medium text-gray-700 dark:text-gray-300">
                          {perfil.codigo}
                        </div>
                        <div className="text-[9px] text-gray-500 dark:text-gray-400 leading-tight">
                          N{perfil.nivel_acesso}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {telasPorModulo[modulo].map(tela => (
                    <tr key={tela.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-3 py-2 text-sm font-medium text-gray-900 dark:text-white sticky left-0 bg-white dark:bg-gray-800 z-10">
                        {tela.nome}
                      </td>
                      {matriz.perfis.map(perfil => (
                        <td key={`${perfil.id}-${tela.id}`} className="px-2 py-1.5">
                          <div className="flex gap-1 justify-center items-center h-full">
                            {tiposPermissao.map(tipo => {
                              const hasPermissao = getPermissao(perfil.id, tela.id, tipo.key);
                              return (
                                <button
                                  key={tipo.key}
                                  onClick={() => onPermissaoChange(
                                    perfil.id,
                                    tela.id,
                                    tipo.key,
                                    !hasPermissao
                                  )}
                                  className={`w-5 h-5 rounded text-[10px] font-bold transition-all ${
                                    hasPermissao
                                      ? `bg-${tipo.color}-600 text-white shadow-sm`
                                      : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500'
                                  } hover:scale-110`}
                                  title={`${tipo.title}: ${hasPermissao ? 'Ativo' : 'Inativo'}`}
                                >
                                  {tipo.label}
                                </button>
                              );
                            })}
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
